import { formatCurrency, formatDate, LOW_BALANCE_MESSAGES } from '@crm/shared';
import { Button, Card, CardContent, cn, Input, Label, Modal, Select, Spinner } from '@crm/ui';
import { AlertTriangle, CreditCard, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, StatCard, WIDGET_GRID } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { type BillingOrder, useCheckout, useMessagePacks } from '@/modules/billing/api';
import { MessagePacks } from '@/modules/billing/components/message-packs';
import { type CreditEntry, useCredits } from '../api';

const AVISO =
  'O preco das mensagens acompanha a tabela da Meta e pode mudar de um mes para o outro.';

const TIPOS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'TOPUP', label: 'Compra' },
  { value: 'USAGE', label: 'Envio' },
  { value: 'REFUND', label: 'Estorno' },
  { value: 'ADJUSTMENT', label: 'Ajuste' },
];

function Movimento({ entry }: { entry: CreditEntry }) {
  const entrada = entry.amount >= 0;

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{entry.description}</p>
        <p className="text-xs text-muted-foreground">{formatDate(entry.createdAt)}</p>
      </div>

      <span
        className={cn(
          'shrink-0 text-sm font-medium',
          entrada ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
        )}
      >
        {entrada ? '+' : '-'}
        {formatCurrency(Math.abs(entry.amount))}
      </span>
    </li>
  );
}

export function MessagesTab({ includedMessages }: { includedMessages: number | null }) {
  const [tipo, setTipo] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  const credits = useCredits({ type: tipo, from: de, to: ate });
  const packs = useMessagePacks();
  const comprar = useCheckout();
  const [escolhido, setEscolhido] = useState(1000);
  const [comprando, setComprando] = useState(false);

  if (credits.isLoading || packs.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const resumo = credits.data;
  const restantes = resumo?.messagesLeft ?? null;
  const acabando = restantes !== null && restantes < LOW_BALANCE_MESSAGES;
  const opcoes = (packs.data?.data ?? []).filter((pack) => pack.messages > 0);
  const pack = opcoes.find((item) => item.messages === escolhido) ?? null;

  function pagar(order: BillingOrder) {
    if (order.checkoutUrl) window.location.assign(order.checkoutUrl);
  }

  return (
    <div className="space-y-4">
      <div className={WIDGET_GRID.three}>
        <StatCard
          title="Saldo de mensagens"
          shortTitle="Saldo"
          value={restantes === null ? '-' : restantes.toLocaleString('pt-BR')}
          hint={`${formatCurrency(resumo?.balance ?? 0)} em saldo`}
        />
        <StatCard
          title="Preco por mensagem"
          shortTitle="Preco"
          value={resumo?.messagePrice ? formatCurrency(resumo.messagePrice) : '-'}
          hint="descontado a cada envio"
        />
        <StatCard
          title="Mensagens do plano"
          shortTitle="Do plano"
          value={
            includedMessages === null ? 'sem limite' : includedMessages.toLocaleString('pt-BR')
          }
          hint="renovadas todo mes"
        />
      </div>

      {acabando ? (
        <div className="flex gap-2 rounded-card border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <p className="text-xs text-amber-900 dark:text-amber-200">
            O saldo esta acabando. Sem mensagem, o cliente fica sem resposta.
          </p>
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button type="button" className="w-full sm:w-auto" onClick={() => setComprando(true)}>
          <MessageSquare className="h-4 w-4" />
          Comprar mensagens
        </Button>
      </div>

      <Card variant="flush">
        <CardContent className="py-4">
          <div className="grid gap-3 pb-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <div className="space-y-1">
              <Label htmlFor="extrato-tipo" className="text-xs font-normal">
                Tipo
              </Label>
              <Select
                id="extrato-tipo"
                value={tipo}
                onChange={(event) => setTipo(event.target.value)}
              >
                {TIPOS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="extrato-de" className="text-xs font-normal">
                De
              </Label>
              <Input
                id="extrato-de"
                type="date"
                value={de}
                onChange={(event) => setDe(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="extrato-ate" className="text-xs font-normal">
                Ate
              </Label>
              <Input
                id="extrato-ate"
                type="date"
                value={ate}
                onChange={(event) => setAte(event.target.value)}
              />
            </div>
          </div>

          {resumo?.entries.length ? (
            <ul className="divide-y divide-border border-t border-border">
              {resumo.entries.map((entry) => (
                <Movimento key={entry.id} entry={entry} />
              ))}
            </ul>
          ) : (
            <EmptyState icon={<MessageSquare />} title="Nenhum movimento ainda" />
          )}
        </CardContent>
      </Card>

      <Modal
        open={comprando}
        onClose={() => setComprando(false)}
        title="Comprar mensagens"
        subtitle="As mensagens entram no saldo assim que o pagamento e confirmado"
        size="medium"
        actions={[
          { label: 'Cancelar', variant: 'outline', onClick: () => setComprando(false) },
          {
            label: 'Comprar',
            icon: <CreditCard className="h-4 w-4" />,
            disabled: !pack,
            loading: comprar.isPending,
            onClick: () => comprar.mutate({ messages: escolhido }, { onSuccess: pagar }),
          },
        ]}
      >
        <div className="space-y-4">
          <MessagePacks packs={opcoes} escolhido={escolhido} onPick={setEscolhido} />

          <p className="text-xs text-muted-foreground">{AVISO}</p>

          {comprar.error ? (
            <p className="text-sm text-destructive">{toErrorMessage(comprar.error)}</p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
